import { useEffect, useMemo, useRef, useState } from 'react'
import { arcadeCss } from '../arcade/arcadeCss'
import { ArcadeHeader } from '../arcade/atoms'
import { resolveArcadeTheme } from '../arcade/themes'
import { GUIDES, findGuide, searchGuides } from '../guides/registry'
import type { Guide } from '../guides/registry'
import { GUIDE_ARTICLE_CSS } from '../guides/articleStyles'

/** Reads/writes the `/guides` and `/guides/<slug>` path directly, the same
 *  convention `StaffPortal.tsx`'s `tabFromPath`/`pathForTab` already uses for
 *  this host: a plain path match, no react-router `<Route>` param, so the
 *  admin mount (a single `path="/guides/*"` route, see `AppShell.tsx`) and
 *  the portal mount (no react-router at all) can share one function. */
export function slugFromGuidesPath(pathname: string): string | null {
  const m = pathname.match(/\/guides\/([^/]+)\/?$/)
  return m?.[1] ? decodeURIComponent(m[1]) : null
}

export function pathForGuidesSlug(slug: string | null): string {
  return slug ? `/guides/${slug}` : '/guides'
}

/**
 * Attaches the two behaviours the sanitised HTML cannot carry itself:
 *  - a copy button beside every code block (added as real DOM nodes, never
 *    injected as part of the sanitised string -- keeping "content" and "app
 *    chrome" on two sides of the sanitiser is what lets DOMPurify's defaults
 *    stay untouched);
 *  - a placeholder in place of any image that fails to load.
 *
 * The placeholder is the point, not a fallback bolted on after: of the
 * guides' own screenshot markers, more than half have no image yet (the
 * docs teammate is capturing them as CHU-GPT ships each feature), and this
 * section must read as "not captured yet" rather than a broken page. There
 * is deliberately no build-time check for a missing file -- one was
 * considered and dropped, because it would fail every build on this host
 * until the LAST screenshot lands, which is not a trade this section is
 * worth making.
 */
function useGuideContentEffects(containerRef: React.RefObject<HTMLDivElement | null>, html: string) {
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const cleanups: Array<() => void> = []

    container.querySelectorAll('pre').forEach((pre) => {
      const wrapper = document.createElement('div')
      wrapper.className = 'guide-code-block'
      wrapper.style.cssText = 'position: relative;'
      pre.replaceWith(wrapper)
      wrapper.appendChild(pre)

      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = 'Copy'
      button.style.cssText =
        'position: absolute; top: 0.5rem; right: 0.5rem; font-size: 0.7rem; font-weight: 600; ' +
        'padding: 0.25rem 0.6rem; border-radius: 999px; border: none; cursor: pointer; ' +
        'background: var(--a-panel2); color: var(--a-ink-dim);'
      const onClick = () => {
        const codeEl = pre.querySelector('code')
        const text = codeEl ? codeEl.textContent ?? '' : (pre.textContent ?? '')
        void navigator.clipboard
          ?.writeText(text)
          .then(() => {
            button.textContent = 'Copied'
            setTimeout(() => {
              button.textContent = 'Copy'
            }, 1500)
          })
          .catch(() => {
            // Clipboard access can be denied (permissions, insecure
            // context); the code stays selectable by hand regardless.
          })
      }
      button.addEventListener('click', onClick)
      wrapper.appendChild(button)
      cleanups.push(() => button.removeEventListener('click', onClick))
    })

    container.querySelectorAll('img').forEach((img) => {
      const onError = () => {
        const placeholder = document.createElement('div')
        placeholder.setAttribute('role', 'img')
        placeholder.setAttribute('aria-label', img.alt || 'Screenshot not yet captured')
        placeholder.style.cssText =
          'border: 1px dashed var(--a-line); border-radius: 0.6rem; padding: 1.1rem 1.2rem; ' +
          'color: var(--a-ink-dim); font-size: 0.82rem; background: var(--a-panel2);'
        const caption = img.alt.replace(/^screenshot:\s*/i, '')
        placeholder.textContent = caption ? `Screenshot not yet captured: ${caption}` : 'Screenshot not yet captured.'
        img.replaceWith(placeholder)
      }
      img.addEventListener('error', onError)
      cleanups.push(() => img.removeEventListener('error', onError))
    })

    return () => cleanups.forEach((fn) => fn())
  }, [containerRef, html])
}

function GuideIndex({
  guides,
  query,
  onQueryChange,
  activeSlug,
  onSelect,
}: {
  guides: Guide[]
  query: string
  onQueryChange: (q: string) => void
  activeSlug: string | null
  onSelect: (slug: string) => void
}) {
  return (
    <div
      style={{
        background: 'var(--a-panel)',
        border: '1px solid var(--a-line)',
        borderRadius: 'var(--a-radius)',
        boxShadow: 'var(--a-shadow)',
        padding: '1rem',
        width: '16rem',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.7rem',
        alignSelf: 'flex-start',
      }}
    >
      <input
        type="search"
        placeholder="Search guides"
        aria-label="Search guides"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '0.5rem 0.7rem',
          borderRadius: '0.5rem',
          border: '1px solid var(--a-line)',
          background: 'var(--a-panel2)',
          color: 'var(--a-ink)',
          fontSize: '0.85rem',
        }}
      />
      <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }} aria-label="Guides">
        {guides.length === 0 ? (
          <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.82rem', margin: 0 }}>
            {query ? 'No guides match that search.' : 'No guides yet.'}
          </p>
        ) : (
          guides.map((g) => (
            <button
              key={g.slug}
              type="button"
              onClick={() => onSelect(g.slug)}
              style={{
                textAlign: 'left',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.5rem 0.6rem',
                cursor: 'pointer',
                background: g.slug === activeSlug ? 'var(--a-accent)' : 'transparent',
                color: g.slug === activeSlug ? 'var(--a-on-accent)' : 'var(--a-ink)',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{g.title}</div>
              {g.audience ? (
                <div
                  style={{
                    fontSize: '0.72rem',
                    color: g.slug === activeSlug ? 'var(--a-on-accent)' : 'var(--a-ink-dim)',
                    opacity: g.slug === activeSlug ? 0.85 : 1,
                  }}
                >
                  {g.audience}
                </div>
              ) : null}
            </button>
          ))
        )}
      </nav>
    </div>
  )
}

function GuideReader({ guide }: { guide: Guide }) {
  const containerRef = useRef<HTMLDivElement>(null)
  useGuideContentEffects(containerRef, guide.html)

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        gap: '1.2rem',
        alignItems: 'flex-start',
      }}
    >
      {/* The article body is markdown-rendered HTML, so its images, tables and
          code blocks cannot carry React styles; this rule set is what keeps a
          2,624px screenshot inside the column. Found live on 23 Sep: without
          it every capture rendered at its natural width, across the TOC and
          off the page.

          Round 15, Unit 13(a)'s typography pass now lives in
          guides/articleStyles.ts, shared with StaffPortal.tsx's
          PolicyGateScreen -- see that file's own header for why a second
          copy here would have left the policy text embedded in the gate
          screen unstyled. */}
      <style>{GUIDE_ARTICLE_CSS}</style>
      <article
        ref={containerRef}
        className="guide-article"
        style={{
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          background: 'var(--a-panel)',
          border: '1px solid var(--a-line)',
          borderRadius: 'var(--a-radius)',
          boxShadow: 'var(--a-shadow)',
          padding: '1.6rem 1.8rem',
          color: 'var(--a-ink)',
          lineHeight: 1.55,
        }}
        // Sanitised in guides/markdown.ts (DOMPurify) before it ever reaches
        // this component; nothing here re-sanitises or trusts a second time.
        dangerouslySetInnerHTML={{ __html: guide.html }}
      />
      {guide.toc.length > 1 ? (
        /* Readable rather than a column of faint grey (Maroun, 24 Sep 2026):
           its own panel like the index on the left, links in body ink, the
           top-level sections in semibold with their sub-sections indented
           under a hairline so the outline reads as an outline. */
        <nav
          aria-label="On this page"
          style={{
            width: '15rem',
            flexShrink: 0,
            position: 'sticky',
            top: '1rem',
            maxHeight: 'calc(100vh - 2rem)',
            overflowY: 'auto',
            background: 'var(--a-panel)',
            border: '1px solid var(--a-line)',
            borderRadius: 'var(--a-radius)',
            boxShadow: 'var(--a-shadow)',
            padding: '1rem 1.1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.15rem',
            fontFamily: 'var(--a-fb-font)',
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: '0.74rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--a-ink-faint)',
              marginBottom: '0.45rem',
            }}
          >
            On this page
          </div>
          {guide.toc.map((t) => {
            const top = t.depth <= 1
            return (
              <a
                key={t.id}
                href={`#${t.id}`}
                style={{
                  display: 'block',
                  color: top ? 'var(--a-ink)' : 'var(--a-ink-faint)',
                  fontWeight: top ? 600 : 400,
                  fontSize: top ? '0.9rem' : '0.86rem',
                  lineHeight: 1.4,
                  textDecoration: 'none',
                  padding: top ? '0.35rem 0.5rem' : '0.25rem 0.5rem 0.25rem 0.7rem',
                  marginLeft: top ? 0 : `${(t.depth - 1) * 0.6}rem`,
                  borderLeft: top ? 'none' : '2px solid var(--a-line)',
                  borderRadius: top ? '0.4rem' : 0,
                }}
              >
                {t.text}
              </a>
            )
          })}
        </nav>
      ) : null}
    </div>
  )
}

/**
 * The Guides section: a left index (search + guide list) and a reader, for
 * markdown files the docs teammate writes in `docs/guides/*.md` (see
 * `guides/registry.ts`'s own doc comment for how those reach this bundle).
 * This component renders what is there; it does not write guide content.
 *
 * `chrome`:
 *  - `'embedded'`: assumes an ANCESTOR already provides the `.arcade` root
 *    and its `arcadeCss` (the staff portal's Guides tab, sitting inside
 *    `StaffPortalShell`'s own wrapper) -- renders index+reader only.
 *  - `'standalone'` (default): self-wraps in `.arcade` and injects its own
 *    theme CSS, for a bare mount with no ambient arcade chrome (the admin
 *    nav's `/guides` route). This is the OPPOSITE of which mode self-wraps
 *    on `Leaderboard.tsx`'s own `chrome` prop -- that page always replaces
 *    the whole screen in both mounts, where this one is a tab CONTENT
 *    alongside other portal tabs, never the whole screen, in the portal.
 *
 * Both mounts pin Bento HQ; there is no per-viewer theme choice anywhere on
 * the platform since round 13 removed the cabinet picker.
 */
export function GuidesPage({ chrome = 'standalone' }: { chrome?: 'standalone' | 'embedded' }) {
  const theme = resolveArcadeTheme('bento')
  const [query, setQuery] = useState('')
  const [slug, setSlug] = useState<string | null>(() => slugFromGuidesPath(window.location.pathname))

  const filtered = useMemo(() => searchGuides(query), [query])
  const active = findGuide(slug) ?? GUIDES[0] ?? null

  useEffect(() => {
    if (active) {
      window.history.replaceState(null, '', pathForGuidesSlug(active.slug))
    }
  }, [active])

  function selectGuide(nextSlug: string) {
    setSlug(nextSlug)
  }

  const body = (
    <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start' }}>
      <GuideIndex guides={filtered} query={query} onQueryChange={setQuery} activeSlug={active?.slug ?? null} onSelect={selectGuide} />
      {active ? (
        <GuideReader guide={active} />
      ) : (
        <p style={{ color: 'var(--a-ink-dim)' }}>No guides published yet.</p>
      )}
    </div>
  )

  if (chrome === 'embedded') {
    return body
  }

  return (
    <div className="arcade" style={{ minHeight: '100vh', padding: '1.5rem 1rem' }}>
      <style>{arcadeCss(theme)}</style>
      <div style={{ margin: '0 0 1.2rem' }}>
        <ArcadeHeader theme={theme} title="Guides" subtitle="HOW TO USE THE PLATFORM" />
      </div>
      {body}
    </div>
  )
}

export default GuidesPage
